/**
 * Utility functions for assessment components
 */

import { DesignToken } from '~/client/components/base/core';
import * as GQL from '~/graphql/generated/client';
import { governanceTabFromLabels } from '~/shared/utils/assessment-category';

/**
 * Format a timestamp to a human-readable date string
 * @param timestamp - The timestamp to format (number or string)
 * @returns A formatted date string or 'Not Available' if invalid
 */
export const formatDate = (timestamp: number | string): string => {
  if (!timestamp) {
    return 'Not Available';
  }

  try {
    // Convert to milliseconds if in seconds
    const timestampMs =
      typeof timestamp === 'number' && timestamp < 10000000000
        ? timestamp * 1000
        : Number(timestamp);

    const date = new Date(timestampMs);

    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (e) {
    console.error('Error formatting date:', e);
    return 'Invalid Date';
  }
};

/**
 * Calculate the duration between two timestamps
 * @param startTime - The start timestamp (number or string)
 * @param endTime - The end timestamp (number or string)
 * @returns A formatted duration string or 'Not Available' if invalid
 */
export const calculateDuration = (
  startTime: number | string,
  endTime: number | string
): string => {
  if (!startTime || !endTime) {
    return 'Not Available';
  }

  try {
    // Convert to milliseconds if in seconds
    const startMs =
      typeof startTime === 'number' && startTime < 10000000000
        ? startTime * 1000
        : Number(startTime);
    const endMs =
      typeof endTime === 'number' && endTime < 10000000000
        ? endTime * 1000
        : Number(endTime);

    let durationMs = endMs - startMs;

    // Validate the duration
    if (
      isNaN(durationMs) ||
      durationMs < 0 ||
      durationMs > 24 * 60 * 60 * 1000 * 30
    ) {
      return 'Invalid Duration';
    }

    // Convert to hours, minutes, seconds
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    durationMs -= hours * 1000 * 60 * 60;

    const minutes = Math.floor(durationMs / (1000 * 60));
    durationMs -= minutes * 1000 * 60;

    const seconds = Math.floor(durationMs / 1000);

    // Format the output
    const parts = [];
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds}s`);
    }

    return parts.join(' ');
  } catch (e) {
    console.error('Error calculating duration:', e);
    return 'Invalid Duration';
  }
};

/**
 * Calculate defense rate from MLflow metrics
 * @param mlflowInfo - The MLflow info object containing runs with metrics
 * @returns The calculated defense rate as a percentage
 */
export const calculateDefenseRate = (mlflowInfo: any): number => {
  if (!mlflowInfo?.runs?.length) {
    return 0;
  }

  let totalProbes = 0;
  let totalDefenseRate = 0;

  // Aggregate metrics from individual probes
  mlflowInfo.runs.forEach((run: any) => {
    const metrics = run?.data?.metrics || [];

    // Find all probe-specific metrics
    metrics.forEach((metric: any) => {
      const match = metric.key.match(/^([^/]+)\/([^/]+)$/);
      if (match) {
        const [, probeFullName, metricType] = match;
        if (metricType === 'percent_defended') {
          totalDefenseRate += metric.value || 0;
          totalProbes++;
        }
      }
    });
  });

  // Calculate the average defense rate
  return totalProbes > 0 ? totalDefenseRate / totalProbes : 0;
};

/**
 * Get active MLflow info based on selected version
 * @param mlflowResults - Array of MLflow results
 * @param selectedVersion - Index of the selected version
 * @returns The active MLflow info object or null if not available
 */
export const getActiveMlflowInfo = (
  mlflowResults: any[],
  selectedVersion: number
): any | null => {
  if (!mlflowResults?.length) {
    return null;
  }

  if (selectedVersion !== undefined && mlflowResults.length > selectedVersion) {
    return mlflowResults[selectedVersion];
  }

  // Default to the latest version
  return mlflowResults[mlflowResults.length - 1];
};

/**
 * Extract model type from MLflow run tags
 * @param tags - Array of tags from MLflow run
 * @returns The model type or 'Unknown' if not found
 */
export const getModelTypeFromTags = (
  tags?: { key: string; value: string }[]
): string => {
  if (!tags) {
    return 'Unknown';
  }

  const modelTypeTag = tags.find((tag) => tag.key === 'model.type');
  return modelTypeTag?.value ?? 'Unknown';
};

/**
 * Determine model status based on defense rate thresholds
 * @param defenseRate - The calculated defense rate percentage
 * @returns Object containing status text, icon, and color
 */
export const getModelStatus = (
  defenseRate: number
): {
  text: string;
  icon: string;
  color: string;
} => {
  // Default upper threshold: 80%, default lower threshold: 40%
  if (defenseRate >= 80) {
    return {
      text: 'Safe to Use',
      icon: '✅',
      color: DesignToken.status.info,
    };
  } else if (defenseRate <= 40) {
    return {
      text: 'Not Safe to Use',
      icon: '❌',
      color: DesignToken.status.danger,
    };
  } else {
    return {
      text: 'Use with Caution',
      icon: '⚠️',
      color: DesignToken.status.warning,
    };
  }
};

/**
 * Check if the assessment is an adversarial assessment
 * @param assessment - The assessment object
 * @returns boolean indicating if it's an adversarial assessment
 */
export const isAdversarialAssessment = (assessment: any): boolean => {
  return (
    assessment?.policy?.identifier ===
      'com.fairly.ai.llm.comprehensive.adversarial' ||
    assessment?.policy?.identifier ===
      'com.fairly.ai.security_tracker.configuration'
  );
};

export const isLinkEvidenceAssessment = (assessment: any): boolean => {
  if (!assessment?.policy) {
    return false;
  }

  const labels: string[] = assessment.policy?.labels ?? [];

  const hasLLMAssuranceLabels =
    labels.includes('LLM') && labels.includes('assurance');

  const hasRedTeamAssuranceLabels =
    labels.includes('redteam') && labels.includes('assurance');

  const hasMultiturnAssuranceLabels =
    labels.includes('multiturn') && labels.includes('assurance');

  const isAdversarial = isAdversarialAssessment(assessment);

  return (
    hasLLMAssuranceLabels ||
    hasRedTeamAssuranceLabels ||
    hasMultiturnAssuranceLabels ||
    isAdversarial
  );
};

/** Dashboard governance table buckets (mutually exclusive per assessment). */
export type AssessmentGovernanceTab =
  | 'assessments'
  | 'screening'
  | 'testing'
  | 'monitoring'
  | 'mitigation';

/** True when this assessment is driven by a monitoring-labelled policy. */
export function isMonitoringAssessment(
  assessment: PolicyLabelsSource
): boolean {
  return assessmentPolicyHasLabel(assessment, 'monitoring');
}

type PolicyLabelsSource = {
  policy?: { labels?: readonly (string | null)[] | null } | null;
};

function policyLabelsLowercase(source: PolicyLabelsSource): string[] {
  const labels = source.policy?.labels ?? [];
  return labels
    .filter((l): l is string => typeof l === 'string' && l.length > 0)
    .map((l) => l.toLowerCase());
}

/** Case-insensitive match on policy.labels (e.g. "Assurance" vs "assurance"). */
export function assessmentPolicyHasLabel(
  assessment: PolicyLabelsSource,
  label: string
): boolean {
  const want = label.toLowerCase();
  return policyLabelsLowercase(assessment).includes(want);
}

/**
 * Routes an assessment to a governance tab. Priority: screening → testing →
 * general assessments, so each row appears in exactly one tab. Delegates to the
 * shared classifier so client and server agree on categories.
 */
export function getAssessmentGovernanceTab(
  assessment: PolicyLabelsSource
): AssessmentGovernanceTab {
  return governanceTabFromLabels(assessment.policy?.labels);
}

/**
 * Check if the assessment uses the ISO/IEC TR 24027 Model Test policy
 */
export const isIso24027TestsAssessment = (assessment: any): boolean => {
  return assessment?.policy?.identifier === 'iso.24027';
};

/**
 * Check if the assessment uses the NYC Local Law 144 (NYLL 144) policy
 */
export const isNyll144Assessment = (assessment: any): boolean => {
  return assessment?.policy?.identifier === 'com.fairly.nyll144';
};

export const isPredictiveAiBiasAssessment = (assessment: any): boolean => {
  return assessment?.policy?.identifier === 'com.fairly.predictiveaibiasaudit';
};

export const isFeatureAnalysisAssessment = (assessment: any): boolean => {
  return assessment?.policy?.identifier === 'com.pathward.feature.analysis';
};

export const isCaliforniaFehaAssessment = (assessment: any): boolean => {
  return assessment?.policy?.identifier === 'com.fairly.feha';
};

/**
 * Check if the assessment uses the PII Leakage Testing for LLMs policy
 */
export const isPiiLeakageAssessment = (assessment: any): boolean => {
  return (
    assessment?.policy?.identifier === 'com.fairly.ai.multilingual.pii.tests'
  );
};

// Helper function to check if a card is fully completed
export const isCardFullyCompleted = (
  card: GQL.Card,
  cardType: GQL.CardType
): boolean => {
  const cardFields = cardType.cardFields || [];

  if (cardFields.length === 0) {
    return true;
  }

  // Create map of answered fields
  const answeredFields = new Set<string>();
  card.values.forEach((fieldValue) => {
    let isAnswered = false;

    // Check text value (for text fields)
    if (fieldValue.value && fieldValue.value.trim() !== '') {
      isAnswered = true;
    }

    // Check values array (for checkbox, radio, etc.)
    if (fieldValue.values && fieldValue.values.length > 0) {
      const hasValidValues = fieldValue.values.some(
        (v) => v?.value && v.value.trim() !== ''
      );
      if (hasValidValues) {
        isAnswered = true;
      }
    }

    if (isAnswered) {
      answeredFields.add(fieldValue.cardFieldId);
    }
  });

  // Check if ALL card fields are answered
  return cardFields
    .filter((field) => field.required === true)
    .every((field) => answeredFields.has(field._id.toString()));
};
